import {
  IApiRequest,
  IApiResponse,
  IAutoAssignWorkOrderTemplateStatus,
  IRequiredWorkOrder,
  IWorkOrder,
  IWorkOrderListResponse,
} from "@miplatform/types";
import { API_VERSION, TASK_SERVICE, X_API_VERSION } from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_WORK_ORDERS = `${TASK_SERVICE}/work-orders`;

export const workOrders = {
  getAutoAssignStatuses: async ({
    calendarSetId,
  }: {
    calendarSetId: string;
  }): Promise<IApiResponse<IAutoAssignWorkOrderTemplateStatus[]>> => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/auto-assign/status`,
      params: { calendarSetId },
    });

    return apiRequest(options);
  },
  getAll: async ({ filters, sort, top, skip }: IApiRequest = {}): Promise<
    IApiResponse<IWorkOrderListResponse[]>
  > => {
    const options = await apiOptions({
      filters,
      sort,
      top,
      skip,
      url: API_WORK_ORDERS,
    });

    return apiRequest(options);
  },
  getById: async ({
    id,
  }: {
    id: string;
  }): Promise<IApiResponse<IWorkOrder>> => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/${id}`,
    });

    return apiRequest(options);
  },
  getRequiredWorkOrders: async ({
    id,
  }: {
    id: string;
  }): Promise<IApiResponse<IRequiredWorkOrder[]>> => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/${id}/required-work-orders`,
    });

    return apiRequest(options);
  },
  getHoldOptions: ({ id, data, modifiedOn }: IApiRequest) =>
    apiOptions({
      url: `${API_WORK_ORDERS}/${id}/hold`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    }),
  setLock: async ({
    id,
    data,
    modifiedOn,
    toastMessageConfig,
  }: IApiRequest): Promise<IApiResponse<IWorkOrder>> => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/${id}/lock`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
      modifiedOn
    });

    return apiRequest(options, toastMessageConfig);
  },
  hold: async ({ id, data, modifiedOn, toastMessageConfig }: IApiRequest) => {
    const options = await workOrders.getHoldOptions({ id, data, modifiedOn });

    return apiRequest(options, toastMessageConfig);
  },
  create: async ({
    data,
    toastMessageConfig,
  }: IApiRequest): Promise<IApiResponse> => {
    const options = await apiOptions({
      url: API_WORK_ORDERS,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  getReleaseOptions: ({ id, data, modifiedOn }: IApiRequest) =>
    apiOptions({
      url: `${API_WORK_ORDERS}/${id}/release`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
      modifiedOn,
    }),
  release: async ({
    id,
    data,
    modifiedOn,
    toastMessageConfig,
  }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/${id}/release`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "put",
      data,
      modifiedOn,
    });
    return apiRequest(options, toastMessageConfig);
  },
  getResetOptions: ({ id, modifiedOn }: IApiRequest) =>
    apiOptions({
      url: `${API_WORK_ORDERS}/${id}/reset`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      modifiedOn,
    }),
  autoAssign: async ({ data }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/auto-assign`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "post",
      data,
    });

    return options;
  },
  autoAssignCalendar: async ({ id, data, toastMessageConfig }: IApiRequest) => {
    const options = await apiOptions({
      url: `${API_WORK_ORDERS}/auto-assign/calendar/${id}`,
      headers: { [X_API_VERSION]: API_VERSION },
      method: "post",
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
};
